import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';

const inputStyle = {
  fontSize: '0.8rem',
  padding: '6px 9px',
  borderRadius: 'var(--radius)',
  border: '1px solid var(--border-hover)',
  background: 'var(--surface)',
  color: 'var(--text)',
  outline: 'none',
  fontFamily: 'inherit',
  width: '100%',
};

export default function Dashboard() {
  const { user, logout }       = useAuth();
  const navigate               = useNavigate();
  const [docs,    setDocs]     = useState([]);
  const [team,    setTeam]     = useState([]);
  const [loading, setLoading]  = useState(true);
  const [creating, setCreating] = useState(false);
  const [filter,  setFilter]   = useState('all');
  const [search,  setSearch]   = useState('');

  // ── Add-member modal state ──────────────────────────────────────────────────
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', email: '', password: '', role: 'Viewer' });
  const [addMemberErr, setAddMemberErr] = useState('');
  const [addMemberLoading, setAddMemberLoading] = useState(false);

  const roleLower = (user?.role || 'viewer').toLowerCase();
  const isAdmin   = roleLower === 'admin';
  const canCreate = ['admin', 'editor'].includes(roleLower);

  const fetchData = useCallback(() => {
    const docsReq = api.get('/documents');
    const teamReq = isAdmin ? api.get('/team') : Promise.resolve(null);

    Promise.all([docsReq, teamReq])
      .then(([docsRes, teamRes]) => {
        setDocs(docsRes.data.documents || []);
        if (teamRes) setTeam(teamRes.data.users || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const liveDocs   = docs.filter(d => !!d.isLive);
  const mineDocs   = docs.filter(d => d.owner?._id === user?._id || d.owner === user?._id);
  const onlineTeam = team.filter(m => m.isActive && m._id !== user?._id);

  const filtered = docs.filter(d => {
    const q = search.toLowerCase();
    const matchSearch = (d.title || '').toLowerCase().includes(q);
    if (filter === 'live') return matchSearch && !!d.isLive;
    if (filter === 'mine') return matchSearch && (d.owner?._id === user?._id || d.owner === user?._id);
    return matchSearch;
  });

  const createDoc = async () => {
    setCreating(true);
    try {
      const { data } = await api.post('/documents', { title: 'Untitled Document' });
      navigate(`/documents/${data.document._id}`);
    } catch (e) {
      console.error(e);
      setCreating(false);
    }
  };

  // ── Delete a document (owner or global Admin — mirrors the backend rule
  // in DELETE /api/documents/:id) ────────────────────────────────────────────
  const deleteDoc = async (doc, e) => {
    e.stopPropagation(); // don't trigger the card's onClick navigation
    const label = doc.title || 'Untitled Document';
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/documents/${doc._id}`);
      setDocs(prev => prev.filter(d => d._id !== doc._id));
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Failed to delete document.');
    }
  };

  // ── Role change (admin only) ────────────────────────────────────────────────
  const changeRole = async (userId, newRole) => {
    try {
      const { data } = await api.patch(`/team/${userId}/role`, { role: newRole });
      setTeam(prev => prev.map(m => m._id === userId ? data.user : m));
    } catch (e) {
      console.error(e);
    }
  };

  // ── Activate / deactivate (admin only) ──────────────────────────────────────
  // FIX: this used to call DELETE /team/:id, which was really a soft
  // deactivate wearing a delete's name. Delete now permanently removes the
  // user, so this calls the dedicated status toggle instead — and can also
  // reactivate someone, which the old code had no way to do at all.
  const toggleActive = async (member) => {
    const next = !member.isActive;
    if (!next && !window.confirm(`Deactivate ${member.name}? They won't be able to log in until reactivated.`)) return;
    try {
      const { data } = await api.patch(`/team/${member._id}/status`, { isActive: next });
      setTeam(prev => prev.map(m => m._id === member._id ? data.user : m));
    } catch (e) {
      console.error(e);
    }
  };

  // ── Permanently delete a user (admin only) ──────────────────────────────────
  const deleteMember = async (member) => {
    if (!window.confirm(`Permanently delete ${member.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/team/${member._id}`);
      setTeam(prev => prev.filter(m => m._id !== member._id));
      fetchData(); // their owned/collaborated docs may have changed
    } catch (e) {
      const ownedCount = e.response?.data?.ownedCount;
      if (e.response?.status === 409 && ownedCount) {
        const force = window.confirm(
          `${member.name} owns ${ownedCount} document(s). Delete them too and permanently remove this user?`
        );
        if (force) {
          try {
            await api.delete(`/team/${member._id}?force=true`);
            setTeam(prev => prev.filter(m => m._id !== member._id));
            fetchData();
          } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || 'Failed to delete user.');
          }
        }
      } else {
        console.error(e);
        alert(e.response?.data?.message || 'Failed to delete user.');
      }
    }
  };

  // ── Add a new team member (admin only) ──────────────────────────────────────
  const addMember = async (e) => {
    e.preventDefault();
    setAddMemberErr('');
    setAddMemberLoading(true);
    try {
      const { data } = await api.post('/team', newMember);
      setTeam(prev => [data.user, ...prev]);
      setNewMember({ name: '', email: '', password: '', role: 'Viewer' });
      setShowAddMember(false);
    } catch (err) {
      setAddMemberErr(err.response?.data?.errors?.[0]?.msg || err.response?.data?.message || 'Failed to add member.');
    } finally {
      setAddMemberLoading(false);
    }
  };

  const avatarColor = (id = '') => {
    const colors = ['#4f46e5','#10b981','#f59e0b','#ec4899','#06b6d4','#8b5cf6','#ef4444'];
    let n = 0;
    for (let i = 0; i < id.length; i++) n += id.charCodeAt(i);
    return colors[n % colors.length];
  };

  const initials = (name = '') =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  const roleClass = r =>
    ({ admin: 'role-admin', editor: 'role-editor', viewer: 'role-viewer' }[(r || '').toLowerCase()] || 'role-viewer');

  return (
    <div className="dashboard">

      {/* ── Top bar ── */}
      <header className="dash-header">
        <div className="logo">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          SecureCollab
        </div>

        <div className="dash-search">
          <span className="dash-search-icon">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </span>
          <input
            placeholder="Search documents…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div className="user-info">
          <span className={`role-badge ${roleClass(user?.role)}`}>{user?.role}</span>
          <span className="user-name">{user?.name}</span>
          <button className="btn-ghost" onClick={async () => { await logout(); navigate('/login'); }}>
            Sign out
          </button>
        </div>
      </header>

      <div className="dash-body">

        {/* ── Sidebar ── */}
        <aside className="dash-sidebar">
          <div className="sidebar-section-label">Workspace</div>

          {[
            { label: 'All docs', f: 'all',  count: docs.length,     icon: 'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z' },
            { label: 'Live',     f: 'live', count: liveDocs.length,  icon: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 6v4l3 3' },
            { label: 'Mine',     f: 'mine', count: mineDocs.length,  icon: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z' },
          ].map(({ label, f, count, icon }) => (
            <div
              key={f}
              className={`sidebar-item ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
            >
              <svg className="sidebar-item-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d={icon}/></svg>
              {label}
              <span className="sidebar-badge">{count}</span>
            </div>
          ))}

          <div className="sidebar-divider" />

          {isAdmin && (
            <>
              <div className="sidebar-section-label">Admin</div>
              {/* FIX: this used to call setFilter('all') — clicking "Team" did
                  nothing but reset you to the doc list. It now opens a real
                  team management view in the main panel. */}
              <div className={`sidebar-item ${filter === 'team' ? 'active' : ''}`} onClick={() => setFilter('team')}>
                <svg className="sidebar-item-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                Team
                <span className="sidebar-badge">{team.length}</span>
              </div>
              <div className="sidebar-divider" />
            </>
          )}

          <div className="sidebar-section-label">
            {isAdmin ? 'Team members' : 'Team'}
          </div>

          <div className="sidebar-member">
            <div className="member-avatar online" style={{ background: avatarColor(user?._id || '') }}>
              {initials(user?.name)}
            </div>
            <div>
              <div className="member-name">{user?.name}</div>
              <div className="member-role">{user?.role} · you</div>
            </div>
          </div>

          {isAdmin
            ? team.filter(m => m._id !== user?._id).slice(0, 6).map(m => (
                <div className="sidebar-member" key={m._id}>
                  <div className={`member-avatar ${m.isActive ? 'online' : ''}`} style={{ background: avatarColor(m._id) }}>
                    {initials(m.name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="member-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.name}
                    </div>
                    <div className="member-role">
                      {m.role}{!m.isActive && <span style={{ color: 'var(--danger)' }}> · inactive</span>}
                    </div>
                  </div>
                </div>
              ))
            : [...new Map(
                docs.flatMap(d => d.collaborators || [])
                  .filter(c => (c.user?._id || c.user) !== user?._id)
                  .map(c => [c.user?._id || c.user, c])
              ).values()].slice(0, 5).map((c, i) => (
                <div className="sidebar-member" key={i}>
                  <div className="member-avatar online" style={{ background: avatarColor(c.user?._id || String(i)) }}>
                    {initials(c.user?.name)}
                  </div>
                  <div>
                    <div className="member-name">{c.user?.name || 'Teammate'}</div>
                    <div className="member-role">{c.role}</div>
                  </div>
                </div>
              ))
          }

          {isAdmin && team.length > 7 && (
            <div
              className="sidebar-item"
              style={{ fontSize: '0.72rem', color: 'var(--text3)' }}
              onClick={() => setFilter('team')}
            >
              View all {team.length} members →
            </div>
          )}
        </aside>

        {/* ── Main ── */}
        <main className="dash-main">

          <div className="dash-stats">
            <div className="stat-card">
              <div className="stat-label">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                Total docs
              </div>
              <div className="stat-value">{docs.length}</div>
              <div className="stat-sub">{mineDocs.length} owned by you</div>
            </div>

            <div className="stat-card">
              <div className="stat-label">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                {isAdmin ? 'Team size' : 'Collaborators'}
              </div>
              <div className="stat-value">{isAdmin ? team.length : onlineTeam.length}</div>
              <div className="stat-sub">
                {isAdmin ? `${team.filter(m => m.isActive).length} active` : 'on shared docs'}
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-label">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Live edits
              </div>
              <div className="stat-value">{liveDocs.length}</div>
              <div className="stat-sub">docs being edited</div>
            </div>

            <div className="stat-card">
              <div className="stat-label">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                Your role
              </div>
              <div className="stat-value" style={{ fontSize: '0.95rem', paddingTop: '3px' }}>
                <span className={`role-badge ${roleClass(user?.role)}`}>{user?.role}</span>
              </div>
              <div className="stat-sub">
                {roleLower === 'admin' ? 'Full access' : roleLower === 'editor' ? 'Can edit docs' : 'View only'}
              </div>
            </div>
          </div>

          {filter === 'team' && isAdmin ? (
            <>
              <div className="dash-toolbar">
                <h2>
                  Team management
                  <span style={{ fontWeight: 400, color: 'var(--text3)', fontSize: '0.78rem', marginLeft: '8px' }}>
                    {team.length} {team.length === 1 ? 'member' : 'members'}
                  </span>
                </h2>
                <button className="btn-primary" onClick={() => setShowAddMember(true)}>
                  + Add member
                </button>
              </div>

              <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--surface-2, rgba(0,0,0,0.02))', textAlign: 'left' }}>
                      <th style={{ padding: '10px 14px' }}>Name</th>
                      <th style={{ padding: '10px 14px' }}>Email</th>
                      <th style={{ padding: '10px 14px' }}>Role</th>
                      <th style={{ padding: '10px 14px' }}>Status</th>
                      <th style={{ padding: '10px 14px', textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {team.map(m => {
                      const isSelf = m._id === user?._id;
                      return (
                        <tr key={m._id} style={{ borderTop: '1px solid var(--border)' }}>
                          <td style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div className={`member-avatar ${m.isActive ? 'online' : ''}`} style={{ background: avatarColor(m._id), width: 24, height: 24, fontSize: '0.65rem' }}>
                              {initials(m.name)}
                            </div>
                            {m.name}{isSelf && <span style={{ color: 'var(--text3)' }}> (you)</span>}
                          </td>
                          <td style={{ padding: '10px 14px', color: 'var(--text3)' }}>{m.email}</td>
                          <td style={{ padding: '10px 14px' }}>
                            <select
                              value={m.role}
                              onChange={e => changeRole(m._id, e.target.value)}
                              disabled={isSelf}
                              style={{ ...inputStyle, width: 'auto', padding: '4px 7px' }}
                            >
                              <option value="Admin">Admin</option>
                              <option value="Editor">Editor</option>
                              <option value="Viewer">Viewer</option>
                            </select>
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            <span className={`role-badge ${m.isActive ? 'role-editor' : 'role-viewer'}`}>
                              {m.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {!isSelf && (
                              <>
                                <button
                                  className="btn-ghost"
                                  style={{ fontSize: '0.72rem', padding: '4px 8px' }}
                                  onClick={() => toggleActive(m)}
                                >
                                  {m.isActive ? 'Deactivate' : 'Reactivate'}
                                </button>
                                <button
                                  className="btn-ghost"
                                  style={{ fontSize: '0.72rem', padding: '4px 8px', color: 'var(--danger)' }}
                                  onClick={() => deleteMember(m)}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <div className="dash-toolbar">
                <h2>
                  {filter === 'all' ? 'All documents' : filter === 'live' ? 'Live documents' : 'My documents'}
                  <span style={{ fontWeight: 400, color: 'var(--text3)', fontSize: '0.78rem', marginLeft: '8px' }}>
                    {filtered.length} {filtered.length === 1 ? 'doc' : 'docs'}
                  </span>
                </h2>
                <div className="filter-group">
                  {['all', 'live', 'mine'].map(f => (
                    <button key={f} className={`filter-btn ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
                      {f === 'all' ? 'All' : f === 'live' ? 'Live' : 'Mine'}
                    </button>
                  ))}
                </div>
                {canCreate && (
                  <button className="btn-primary" onClick={createDoc} disabled={creating}>
                    + {creating ? 'Creating…' : 'New doc'}
                  </button>
                )}
              </div>

              <div className="doc-grid">
                {loading ? (
                  <div style={{ gridColumn: '1/-1', padding: '3rem', textAlign: 'center', color: 'var(--text3)' }}>
                    <div className="loading-spinner" style={{ margin: '0 auto 0.75rem' }} />
                    Loading documents…
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    </div>
                    <h3>{search ? `No results for "${search}"` : 'No documents here'}</h3>
                    <p>
                      {search ? 'Try a different search term.' :
                       filter === 'live' ? 'No documents are being edited right now.' :
                       filter === 'mine' ? "You haven't created any documents yet." :
                       canCreate ? 'Create your first document to get started.' : 'No documents shared with you yet.'}
                    </p>
                    {canCreate && !search && filter !== 'live' && (
                      <button className="btn-primary" onClick={createDoc} disabled={creating}>
                        + New document
                      </button>
                    )}
                  </div>
                ) : (
                  filtered.map(doc => {
                    const collabs = (doc.collaborators || []).slice(0, 3);
                    const ownerId = doc.owner?._id || doc.owner || '';
                    // FIX: mirrors the backend rule in DELETE /api/documents/:id
                    // exactly — owner OR global Admin role. A doc-level "Admin"
                    // collaborator who isn't the actual owner still gets a 403
                    // from the server, so we don't show the button to them either.
                    const canDeleteThisDoc = ownerId === user?._id || isAdmin;
                    return (
                      <div
                        key={doc._id}
                        className={`doc-card ${doc.isLive ? 'live' : ''}`}
                        onClick={() => navigate(`/documents/${doc._id}`)}
                      >
                        <div className="doc-card-icon" style={{ background: '#eeedfe' }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#534AB7" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                          </svg>
                        </div>
                        <h3>{doc.title || 'Untitled'}</h3>
                        <div className="doc-type">
                          By {doc.owner?.name || 'Unknown'} · v{doc.version ?? 1}
                        </div>
                        {!!doc.isLive && (
                          <div className="live-badge">
                            <span className="live-dot" /> Live
                          </div>
                        )}
                        <div className="doc-footer">
                          <span className="doc-date">
                            {new Date(doc.updatedAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                          </span>
                          <div className="doc-avatars">
                            <div className="doc-av" style={{ background: avatarColor(ownerId) }} title={doc.owner?.name}>
                              {initials(doc.owner?.name)}
                            </div>
                            {collabs.map((c, i) => (
                              <div key={i} className="doc-av" style={{ background: avatarColor(c.user?._id || String(i)) }} title={c.user?.name}>
                                {initials(c.user?.name)}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* ── Delete action — only shown to the owner or a global Admin ── */}
                        {canDeleteThisDoc && (
                          <button
                            className="btn-ghost"
                            style={{
                              marginTop: '10px',
                              width: '100%',
                              justifyContent: 'center',
                              fontSize: '0.72rem',
                              padding: '5px 8px',
                              color: 'var(--danger)',
                            }}
                            onClick={(e) => deleteDoc(doc, e)}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6l-2 14H7L5 6"/>
                              <path d="M10 11v6M14 11v6"/>
                              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                            </svg>
                            Delete
                          </button>
                        )}
                      </div>
                    );
                  })
                )}

                {!loading && canCreate && filtered.length > 0 && (
                  <div className="doc-card-new" onClick={createDoc}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    New document
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {/* ── Add member modal ── */}
      {showAddMember && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
          onClick={() => setShowAddMember(false)}
        >
          <div
            className="auth-card"
            style={{ width: '340px', maxWidth: '90vw' }}
            onClick={e => e.stopPropagation()}
          >
            <h1 style={{ fontSize: '1.1rem' }}>Add team member</h1>
            <p className="subtitle">Creates their account directly — share the password with them.</p>

            <form onSubmit={addMember} style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
              <input
                style={inputStyle}
                placeholder="Full name"
                value={newMember.name}
                onChange={e => setNewMember(f => ({ ...f, name: e.target.value }))}
                required
                autoFocus
              />
              <input
                style={inputStyle}
                type="email"
                placeholder="Email address"
                value={newMember.email}
                onChange={e => setNewMember(f => ({ ...f, email: e.target.value }))}
                required
              />
              <input
                style={inputStyle}
                type="password"
                placeholder="Temporary password (min. 6 chars)"
                value={newMember.password}
                onChange={e => setNewMember(f => ({ ...f, password: e.target.value }))}
                required
              />
              <select
                style={inputStyle}
                value={newMember.role}
                onChange={e => setNewMember(f => ({ ...f, role: e.target.value }))}
              >
                <option value="Viewer">Viewer</option>
                <option value="Editor">Editor</option>
                <option value="Admin">Admin</option>
              </select>

              {addMemberErr && <div className="error">{addMemberErr}</div>}

              <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                <button type="button" className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setShowAddMember(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" style={{ flex: 1, justifyContent: 'center' }} disabled={addMemberLoading}>
                  {addMemberLoading ? 'Adding…' : 'Add member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}